import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import Image from 'next/image'

import { useTranslation } from '../../contexts/I18nContext'
import { getCurrentDomain, encodeRecipeSlug } from '../../helpers'
import RecipeCard from '../../components/RecipeCard'
import RecipeCookingSupporting from '../../components/RecipeCookingSupporting'
import Grid from '../../components/Grid'
import Meta from '../../components/Meta'

import Styled from './IngredientPage.styled'

/**
 * Format data types for crawlers: recipe carousel
 * https://developers.google.com/search/docs/advanced/structured-data/recipe
 */
const getRecipeCarouselStructuredData = ({ router, recipes }) => {
  const currentLocale =
    router.locale !== router.defaultLocale ? `/${router.locale}` : ''
  const currentUrl = `${getCurrentDomain()}${currentLocale}${
    router.asPath.split(/[?#]/)[0]
  }`
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    url: currentUrl,
    numberOfItems: `${recipes.length}`,
    itemListElement: recipes.map((recipe, index) => ({
      '@type': 'ListItem',
      position: `${index + 1}`,
      url: `${getCurrentDomain()}${currentLocale}/recipes/${encodeRecipeSlug({
        slug: recipe.slug,
        id: recipe.id,
      })}`,
    })),
  })
}

const IngredientPage = ({ ingredient, recipes, auth }) => {
  const router = useRouter()
  const { t } = useTranslation()
  const [homeSlug, setHomeSlug] = useState('/?modal=onboarding')
  useEffect(() => {
    if (auth.accessToken) {
      setHomeSlug('/home')
    }
  }, [auth.accessToken])

  return (
    <>
      <Meta
        title={t('META.INGREDIENT.TITLE', { ingredient: ingredient.name })}
        description={t('META.INGREDIENT.DESCRIPTION', {
          ingredient: ingredient.name,
        })}
        imageUrl={t('META.INGREDIENT.IMAGE_URL')}
      >
        {recipes.length > 1 ? (
          <script
            key="recipe-carousel-structured-data"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: getRecipeCarouselStructuredData({
                router,
                recipes,
              }),
            }}
          />
        ) : null}
      </Meta>
      <Styled>
        <Styled.header>
          <Styled.title>
            {`${t('INGREDIENT_PAGE.TITLE')} `}
            <strong>{ingredient.name}</strong>
          </Styled.title>
          <Styled.description>
            {t('INGREDIENT_PAGE.DESCRIPTION')}
          </Styled.description>
          <Styled.discover to={homeSlug}>
            {t(homeSlug === '/home' ? 'LETS_SHOP' : 'INGREDIENT_PAGE.DISCOVER')}
          </Styled.discover>
        </Styled.header>
        <Styled.page>
          <Styled.content>
            <Styled.ingredient>
              <Styled.image>
                <Image
                  src={`${process.env.NEXT_PUBLIC_ASSETS_URL}/${ingredient.imageUrl}`}
                  width={128}
                  height={128}
                  alt={ingredient.name}
                />
              </Styled.image>
              <Styled.ingredientName>{ingredient.name}</Styled.ingredientName>
              {ingredient.editorialData &&
              ingredient.editorialData.description ? (
                <Styled.ingredientDescription>
                  {ingredient.editorialData.description}
                </Styled.ingredientDescription>
              ) : null}
              {ingredient.editorialData &&
              ingredient.editorialData.averageEstimatedValues &&
              ingredient.editorialData.nutritionalFacts ? (
                <>
                  <Styled.nutritionalFactsTitle>
                    {t('INGREDIENT_PAGE.NUTRITION_FACTS')}
                  </Styled.nutritionalFactsTitle>
                  <Styled.nutritionalFactsExplanation>
                    {t('INGREDIENT_PAGE.NUTRITION_FACTS_EXPLANATION')}
                    &nbsp;
                    {ingredient.editorialData.averageEstimatedValues.amount}
                    &nbsp;
                    {ingredient.editorialData.averageEstimatedValues.unit ||
                      t('INGREDIENT_PAGE.NUTRITION_FACTS_PIECE')}
                  </Styled.nutritionalFactsExplanation>
                  <Styled.nutritionalFacts
                    nutritionalFacts={ingredient.editorialData.nutritionalFacts}
                  />
                </>
              ) : null}
              <Styled.discoverJow to={homeSlug}>
                {t(
                  homeSlug === '/home'
                    ? 'LETS_SHOP'
                    : 'INGREDIENT_PAGE.DISCOVER_JOW',
                )}
              </Styled.discoverJow>
            </Styled.ingredient>
            <Styled.recipes>
              <Grid variant="recipe-card">
                {recipes.map((recipe) => (
                  <RecipeCard
                    key={recipe.id}
                    imageUrl={recipe.imageUrl}
                    title={recipe.title}
                    description={recipe.composition}
                    id={recipe.id}
                    slug={recipe.slug}
                    source={recipe.source}
                    tagsEdito={recipe.tagsEdito}
                    coversCount={recipe.coversCount}
                    roundedCoversCount={recipe.roundedCoversCount}
                    unitName={recipe.unitName}
                    missingIngredients={recipe.missingIngredients}
                    backgroundColor={recipe.backgroundColor}
                  >
                    <RecipeCookingSupporting
                      id={recipe.id}
                      title={recipe.title}
                      favorites={[]}
                      slug={recipe.slug}
                      coversCount={recipe.coversCount}
                      source={recipe.source}
                    />
                  </RecipeCard>
                ))}
              </Grid>
            </Styled.recipes>
          </Styled.content>
        </Styled.page>
      </Styled>
    </>
  )
}

export default IngredientPage
